/**
 * WebSocket context provider.
 *
 * Initializes WebSocket on auth, disconnects on logout.
 * Provides subscribe/unsubscribe/onMessage to children.
 * Wires "notification.created" to the notification store + Snackbar toast.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useWebSocket } from '@/hooks/useWebSocket';
import { useNotificationStore } from '@/stores/notificationStore';
import { useUIStore } from '@/stores/uiStore';

type MessageHandler = (payload: Record<string, unknown>) => void;

interface WebSocketContextValue {
  isConnected: boolean;
  subscribe: (channel: string) => void;
  unsubscribe: (channel: string) => void;
  onMessage: (type: string, handler: MessageHandler) => () => void;
}

const WebSocketContext = createContext<WebSocketContextValue>({
  isConnected: false,
  subscribe: () => {},
  unsubscribe: () => {},
  onMessage: () => () => {},
});

export function useWebSocketContext(): WebSocketContextValue {
  return useContext(WebSocketContext);
}

interface WebSocketProviderProps {
  children: ReactNode;
}

export function WebSocketProvider({ children }: WebSocketProviderProps) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const { isConnected, subscribe, unsubscribe, onMessage } = useWebSocket({
    token: accessToken,
    enabled: isAuthenticated,
  });

  const incrementUnreadCount = useNotificationStore(
    (s) => s.incrementUnreadCount,
  );
  const addNotification = useNotificationStore((s) => s.addNotification);
  const showInfo = useUIStore((s) => s.showInfo);

  // Wire notification.created to store + toast
  useEffect(() => {
    const unsub = onMessage(
      'notification.created',
      (payload: Record<string, unknown>) => {
        incrementUnreadCount();

        // Add to local cache
        addNotification({
          id: (payload.id as string) ?? '',
          userId: '',
          type: ((payload.notification_type as string) ?? 'task_assigned') as import('@/types/notification.types').NotificationType,
          title: (payload.title as string) ?? 'New notification',
          message: (payload.message as string) ?? null,
          entityType: (payload.entity_type as string) ?? null,
          entityId: (payload.entity_id as string) ?? null,
          isRead: false,
          readAt: null,
          createdAt: new Date().toISOString(),
        });

        // Show MUI Snackbar toast via uiStore
        showInfo(
          (payload.title as string) ?? 'New notification',
          (payload.message as string) ?? undefined,
        );
      },
    );
    return unsub;
  }, [onMessage, incrementUnreadCount, addNotification, showInfo]);

  const value = useMemo(
    () => ({ isConnected, subscribe, unsubscribe, onMessage }),
    [isConnected, subscribe, unsubscribe, onMessage],
  );

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}
